import React, { useState, useEffect, useRef, useMemo } from "react";
import { ArrowLeft, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { createLightLeakageLabel } from "../../services/apiService";
import { decodedToken, getAuthToken } from "../../utils/auth";
import Swal from "sweetalert2";

const LightLeakageLabeling = () => {
  const { state } = useLocation();
  const {
    productInfo,
    imageUrl,
    rootCauses: passedRootCauses = [],
    recordingNotes: passedNotes = "",
    rectangles: passedRectangles = [],
    yoloAnnotations: passedYoloAnnotations = [],
    imageDimensions: passedImageDimensions = { width: 0, height: 0 },
    canvasTransform: passedCanvasTransform = { scale: 1, offsetX: 0, offsetY: 0, scaledWidth: 0, scaledHeight: 0 },
  } = state || {};

  const [result, setResult] = useState("Fail");
  const [rootCauses, setRootCauses] = useState(passedRootCauses);
  const [recordingNotes, setRecordingNotes] = useState(
    passedNotes ||
      passedRootCauses
        .map((rc) => `${rc.leakageType}: ${rc.cause}`)
        .join("\n")
  );
  const [rectangles, setRectangles] = useState(passedRectangles);
  const [yoloAnnotations, setYoloAnnotations] = useState(passedYoloAnnotations);
  const [imageDimensions, setImageDimensions] = useState({
    width: passedImageDimensions.width,
    height: passedImageDimensions.height,
    displayedWidth: 0,
    displayedHeight: 0,
    thumbnailWidth: 0,
    thumbnailHeight: 0,
  });
  const [canvasTransform, setCanvasTransform] = useState(passedCanvasTransform);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const thumbnailCanvasRef = useRef(null);
  const modalCanvasRef = useRef(null);
  const navigate = useNavigate();

  // Check token expiration on mount and redirect to /login if expired
  useEffect(() => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.warn("No token found, redirecting to login");
        navigate("/login", { replace: true });
        return;
      }

      const decoded = decodedToken();
      if (!decoded || !decoded.email || !decoded.exp) {
        console.warn("Invalid token, redirecting to login");
        navigate("/login", { replace: true });
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      if (decoded.exp < currentTime) {
        console.warn("Token expired, redirecting to login");
        navigate("/login", { replace: true });
        return;
      }
    } catch (error) {
      console.error("Error validating token:", error.message);
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Compute whether the submit button should be enabled
  const canSubmit = useMemo(() => {
    try {
      const decoded = decodedToken();
      if (!decoded || !decoded.email) return false;

      if (!imageUrl) return false;

      if (!productInfo || !productInfo.productType || !productInfo.modelName) return false;

      if (rootCauses.length === 0) return false;

      // Validate each root cause
      return rootCauses.every((rc, index) => {
        const rectangleId = rc.rectangleIds?.[0];
        if (!rectangleId && rectangleId !== 0) return false;

        const ann = yoloAnnotations[rectangleId];
        if (!ann) return false;

        const bbox = [
          Number.isFinite(ann.x_center) ? ann.x_center : 0,
          Number.isFinite(ann.y_center) ? ann.y_center : 0,
          Number.isFinite(ann.width) ? ann.width : 0,
          Number.isFinite(ann.height) ? ann.height : 0,
        ];

        return bbox.every((coord) => Number.isFinite(coord) && coord >= 0 && coord <= 1);
      });
    } catch (error) {
      console.error("Error checking canSubmit:", error.message);
      return false;
    }
  }, [imageUrl, productInfo, rootCauses, yoloAnnotations]);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    setIsAndroid(/android/.test(userAgent));
  }, []);

  useEffect(() => {
    console.log("Incoming state:", state);
    if (state) {
      setRootCauses(state.rootCauses || []);
      setRecordingNotes(state.recordingNotes || "");
      setRectangles(state.rectangles || []);
      setYoloAnnotations(state.yoloAnnotations || []);
      setImageDimensions((prev) => ({
        ...prev,
        width: state.imageDimensions?.width || prev.width,
        height: state.imageDimensions?.height || prev.height,
      }));
      setCanvasTransform(state.canvasTransform || canvasTransform);
    }
  }, [state]);

  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setImageDimensions((prev) => ({
        ...prev,
        width: img.width,
        height: img.height,
      }));
      redrawCanvases(img);
    };
  }, [imageUrl, yoloAnnotations, rootCauses, rectangles]);

  useEffect(() => {
    const updateCanvasDimensions = () => {
      if (thumbnailCanvasRef.current) {
        const canvas = thumbnailCanvasRef.current;
        const container = canvas.parentElement;
        const canvasWidth = container.clientWidth;
        const canvasHeight = container.clientHeight;
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        setImageDimensions((prev) => ({
          ...prev,
          thumbnailWidth: canvasWidth,
          thumbnailHeight: canvasHeight,
        }));
        const img = new Image();
        img.src = imageUrl;
        img.onload = () => redrawCanvases(img);
      }
      if (modalCanvasRef.current && isPreviewOpen) {
        const canvas = modalCanvasRef.current;
        const maxWidth = window.innerWidth * 0.9;
        const maxHeight = window.innerHeight * 0.9;
        const scale = Math.min(
          maxWidth / imageDimensions.width,
          maxHeight / imageDimensions.height
        );
        const canvasWidth = imageDimensions.width * scale;
        const canvasHeight = imageDimensions.height * scale;
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        setImageDimensions((prev) => ({
          ...prev,
          displayedWidth: canvasWidth,
          displayedHeight: canvasHeight,
        }));
        const img = new Image();
        img.src = imageUrl;
        img.onload = () => redrawCanvases(img);
      }
    };

    updateCanvasDimensions();
    window.addEventListener("resize", updateCanvasDimensions);

    return () => {
      window.removeEventListener("resize", updateCanvasDimensions);
    };
  }, [imageUrl, isPreviewOpen, imageDimensions.width, imageDimensions.height]);

  const redrawCanvases = (img) => {
    if (thumbnailCanvasRef.current) {
      const ctx = thumbnailCanvasRef.current.getContext("2d");
      drawCanvas(ctx, img, false);
    }
    if (modalCanvasRef.current && isPreviewOpen) {
      const ctx = modalCanvasRef.current.getContext("2d");
      drawCanvas(ctx, img, true);
    }
  };

  const drawCanvas = (ctx, img, isModal) => {
    const canvas = ctx.canvas;
    const imgWidth = imageDimensions.width;
    const imgHeight = imageDimensions.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!isAndroid && !isModal) {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      yoloAnnotations.forEach((ann, idx) => {
        const isAssociated = rootCauses.some((rc) =>
          rc.rectangleIds?.includes(idx)
        );
        if (!isAssociated) return;

        const rect = {
          x: (ann.x_center - ann.width / 2) * imgWidth,
          y: (ann.y_center - ann.height / 2) * imgHeight,
          width: ann.width * imgWidth,
          height: ann.height * imgHeight,
          color: ann.color || "#ffffff",
        };

        const canvasRectX = (rect.x / imgWidth) * canvas.width;
        const canvasRectY = (rect.y / imgHeight) * imgHeight;
        const canvasRectWidth = (rect.width / imgWidth) * canvas.width;
        const canvasRectHeight = (rect.height / imgHeight) * canvas.height;

        ctx.strokeStyle = rect.color;
        ctx.lineWidth = 2;
        ctx.strokeRect(
          canvasRectX,
          canvasRectY,
          canvasRectWidth,
          canvasRectHeight
        );
      });
    } else {
      const containerWidth = isModal
        ? imageDimensions.displayedWidth || imageDimensions.width
        : imageDimensions.thumbnailWidth || canvas.width;
      const containerHeight = isModal
        ? imageDimensions.displayedHeight || imageDimensions.height
        : imageDimensions.thumbnailHeight || canvas.height;

      const scale = Math.min(containerWidth / imgWidth, containerHeight / imgHeight);
      const scaledWidth = imgWidth * scale;
      const scaledHeight = imgHeight * scale;
      const offsetX = (canvas.width - scaledWidth) / 2;
      const offsetY = (canvas.height - scaledHeight) / 2;

      ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

      yoloAnnotations.forEach((ann, idx) => {
        const isAssociated = rootCauses.some((rc) =>
          rc.rectangleIds?.includes(idx)
        );
        if (!isAssociated) return;

        const rect = {
          x: (ann.x_center - ann.width / 2) * imgWidth,
          y: (ann.y_center - ann.height / 2) * imgHeight,
          width: ann.width * imgWidth,
          height: ann.height * imgHeight,
          color: ann.color || "#ffffff",
        };

        ctx.strokeStyle = rect.color;
        ctx.lineWidth = 2;
        ctx.strokeRect(
          offsetX + rect.x * scale,
          offsetY + rect.y * scale,
          rect.width * scale,
          rect.height * scale
        );
      });
    }
  };

  const handleBack = () => {
    navigate("/light-leakage-detector-camera", {
      state: {
        productInfo,
        imageUrl,
        rootCauses,
        recordingNotes,
        rectangles,
        yoloAnnotations,
        imageDimensions,
        canvasTransform,
      },
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const decoded = decodedToken();
      if (!decoded || !decoded.email) {
        throw new Error("Invalid or expired token. Please log in again.");
      }
      const email = decoded.email;

      if (!imageUrl) {
        throw new Error("No image URL provided.");
      }

      if (!productInfo || !productInfo.productType || !productInfo.modelName) {
        throw new Error("Product information is incomplete.");
      }

      const LIGHT_LEAKAGE_TYPE_TO_ID = {
        pressing: "41",
        dark_state: "42",
      };

      const defects = rootCauses.map((rc, index) => {
        const lightLeakageTypeLower = rc.leakageType.toLowerCase().replace(/\s+/g, "_");
        const lightLeakageLabId = LIGHT_LEAKAGE_TYPE_TO_ID[lightLeakageTypeLower] ?? "52";

        const rectangleId = rc.rectangleIds?.[0];
        if (!rectangleId && rectangleId !== 0) {
          throw new Error(`No valid rectangle ID for root cause at index ${index}`);
        }

        const ann = yoloAnnotations[rectangleId];
        if (!ann) {
          throw new Error(`Invalid annotation for rectangle ID ${rectangleId} at index ${index}`);
        }

        const bbox = [
          Number.isFinite(ann.x_center) ? ann.x_center : 0,
          Number.isFinite(ann.y_center) ? ann.y_center : 0,
          Number.isFinite(ann.width) ? ann.width : 0,
          Number.isFinite(ann.height) ? ann.height : 0,
        ];

        return {
          lightLeakageType: lightLeakageTypeLower,
          lightLeakageLabId,
          lightLeakageLabelIssue: {
            lightLeakageRootCause: Array.isArray(rc.cause) ? rc.cause : [rc.cause],
          },
          bbox,
        };
      });

      if (defects.length === 0) {
        throw new Error("No defects specified. Please add at least one root cause.");
      }

      const lightLeakageLabeling = { defects };

      const formData = new FormData();
      formData.append("email", email);
      formData.append("productInfo", JSON.stringify(productInfo));
      formData.append("lightLeakageLabeling", JSON.stringify(lightLeakageLabeling));

      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      const blob = await response.blob();
      const mimeType = blob.type.startsWith("image/") ? blob.type : "image/jpeg";
      const fileExt = mimeType.split("/")[1];
      const filename = `upload.${fileExt}`;
      const imageFile = new File([blob], filename, { type: mimeType });
      formData.append("rawimage", imageFile);

      await createLightLeakageLabel(formData);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Data submitted successfully!",
        confirmButtonText: "OK",
        background: "white",
        customClass: {
          popup: "text-black",
          confirmButton: "bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded",
        },
      });

      navigate("/light-leakage-product-info");
    } catch (error) {
      console.error("Submission Error:", error.message);
      let userMessage = error.message;
      if (error.message.includes("Failed to fetch") || error.message.includes("Network Error")) {
        userMessage = "Network error: Unable to connect to the server. Please check if the backend is running.";
      } else if (error.message.includes("Authentication error")) {
        userMessage = "Authentication failed. Please log in again.";
        navigate("/login", { replace: true }); // Redirect on auth error
      } else if (error.message.includes("Invalid data")) {
        userMessage = "Invalid data submitted. Please check your inputs and try again.";
      }

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: userMessage,
        confirmButtonText: "OK",
        background: "white",
        customClass: {
          popup: "text-black",
          confirmButton: "bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveRootCause = (rootCause) => {
    const targetIndex = rootCauses.findIndex(
      (rc) =>
        rc.leakageType === rootCause.leakageType &&
        rc.cause === rootCause.cause &&
        JSON.stringify(rc.rectangleIds) === JSON.stringify(rootCause.rectangleIds)
    );

    if (targetIndex === -1) {
      console.error("Root cause not found:", rootCause);
      return;
    }

    const newRootCauses = rootCauses.filter((_, idx) => idx !== targetIndex);
    const rectanglesToRemove = rootCause.rectangleIds || [];

    // Remove associated rectangles
    const newRectangles = rectangles.filter(
      (_, idx) => !rectanglesToRemove.includes(idx)
    );

    // Remove associated YOLO annotations
    const newYoloAnnotations = yoloAnnotations.filter(
      (_, idx) => !rectanglesToRemove.includes(idx)
    );

    // Update rectangleIds in remaining root causes
    const updatedRootCauses = newRootCauses.map((rc) => {
      if (!rc.rectangleIds) return rc;
      const newRectangleIds = rc.rectangleIds
        .map((oldIdx) => {
          const removedBefore = rectanglesToRemove.filter(
            (rIdx) => rIdx < oldIdx
          ).length;
          const newIdx = oldIdx - removedBefore;
          return newIdx >= 0 && newIdx < newYoloAnnotations.length ? newIdx : null;
        })
        .filter((idx) => idx !== null);
      return { ...rc, rectangleIds: newRectangleIds };
    });

    // Update recording notes
    const rootCauseEntry = `${rootCause.leakageType}: ${rootCause.cause}`;
    const newRecordingNotes = recordingNotes
      .split("\n")
      .filter((line) => line.trim() !== rootCauseEntry)
      .join("\n");

    setRootCauses(updatedRootCauses);
    setRectangles(newRectangles);
    setYoloAnnotations(newYoloAnnotations);
    setRecordingNotes(newRecordingNotes);
  };

  const togglePreview = () => {
    setIsPreviewOpen(!isPreviewOpen);
  };

  const handleLabelingClick = () => {
    navigate("/light-leakage-inspection", {
      state: {
        productInfo,
        imageUrl,
        rootCauses,
        recordingNotes,
        rectangles,
        yoloAnnotations,
        imageDimensions,
        canvasTransform,
      },
    });
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="flex items-center mb-6">
        <button className="mr-4" onClick={handleBack}>
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-medium">Light Leakage Labeling</h1>
      </div>

      <div className="space-y-6">
        <div
          className="w-full h-64 bg-gray-900 rounded-lg relative overflow-hidden cursor-pointer"
          onClick={togglePreview}
        >
          {imageUrl && imageDimensions.width > 0 ? (
            <canvas ref={thumbnailCanvasRef} className="w-full h-full" />
          ) : (
            <div className="absolute w-full h-full bg-gradient-to-br from-indigo-900/30 to-purple-900/30 flex items-center justify-center">
              <span>No Image Available</span>
            </div>
          )}
        </div>

        {isPreviewOpen && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <button
              className="absolute top-4 left-4 bg-black/50 p-2 rounded-full"
              onClick={togglePreview}
            >
              <X className="w-6 h-6" />
            </button>
            <div className="relative max-w-full max-h-full">
              <canvas ref={modalCanvasRef} className="max-w-full max-h-[90vh]" />
            </div>
          </div>
        )}

        <div>
          <button
            className="w-full bg-blue-600 text-white py-3 rounded-lg"
            onClick={handleLabelingClick}
          >
            Labeling
          </button>
        </div>

        <div>
          <label className="block mb-3">Recording Frame</label>
          <p className="text-sm text-gray-400 mb-4">
            Recorded root causes for the detected defect.
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            {rootCauses.map((rc, idx) => (
              <span
                key={idx}
                className="flex items-center bg-blue-700 text-white px-3 py-1 rounded-full text-sm"
              >
                {`${rc.leakageType}: ${rc.cause}`}
                <button
                  className="ml-2 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveRootCause(rc);
                  }}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>

          <textarea
            className="w-full bg-gray-800 rounded-lg p-4 text-white placeholder-gray-400 mb-4 h-32 resize-none"
            placeholder="Root causes are recorded here..."
            value={recordingNotes}
            onChange={(e) => setRecordingNotes(e.target.value)}
          />

          <button
            onClick={handleSubmit}
            className={`w-full bg-blue-600 text-white py-3 rounded-lg flex items-center justify-center ${
              isSubmitting || !canSubmit ? "opacity-75 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-3 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LightLeakageLabeling;